import { useEffect, useState, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { api, imgUrl, money, normalizeTiers, cartLineTotal, cartUnit } from "@/lib/api";
import { trackVisit } from "@/lib/track";
import { useCart } from "@/context/CartContext";
import StorefrontPopups from "@/components/StorefrontPopups";
import Seo from "@/components/Seo";
import { trackEvent } from "@/lib/analytics";
import {
  Search, ShoppingBag, Heart, Bookmark, X, Plus, Minus, ArrowLeft, Loader2, Lock,
  Instagram, Facebook, Youtube, Twitter, Globe, Mail, Info,
} from "lucide-react";
import { openCookieSettings } from "@/lib/consent";

const CASPER_LOGO = "https://customer-assets-agu9un31.emergentagent.net/job_store-dashboard-69/artifacts/z5d0msg2_LOGO-CASPERBRANDs.png";
const TikTokIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden><path d="M16.6 5.82a4.28 4.28 0 0 1-1.05-2.82h-3.11v12.4a2.53 2.53 0 1 1-2.53-2.53c.26 0 .51.04.75.11V9.8a5.64 5.64 0 1 0 4.89 5.59V9.01a7.35 7.35 0 0 0 4.31 1.38V7.28a4.28 4.28 0 0 1-3.16-1.46Z" /></svg>
);
const SOCIAL_ICON = { instagram: Instagram, tiktok: TikTokIcon, facebook: Facebook, youtube: Youtube, twitter: Twitter, website: Globe, email: Mail };
const SOCIAL_ORDER = ["instagram", "tiktok", "facebook", "youtube", "twitter", "website", "email"];

export default function Storefront() {
  const { handle } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [activeCat, setActiveCat] = useState(null);
  const [selected, setSelected] = useState(null);
  const [liked, setLiked] = useState(new Set());
  const [saved, setSaved] = useState(new Set());
  const [cartOpen, setCartOpen] = useState(false);
  const { cart, addItem, setQty, removeItem, clear, count, total, keyOf } = useCart();
  const [checkingOut, setCheckingOut] = useState(false);
  const [email, setEmail] = useState("");
  const [gatePw, setGatePw] = useState("");
  const [unlocking, setUnlocking] = useState(false);

  useEffect(() => {
    setLoading(true);
    trackVisit(`/${handle}`, handle);
    const params = new URLSearchParams(window.location.search);
    const tokenParam = params.get("token");
    const keyParam = params.get("key");
    const savedTok = tokenParam || sessionStorage.getItem(`sig_store_tok_${handle}`);
    const savedPw = keyParam || sessionStorage.getItem(`sig_store_pw_${handle}`);
    api.get(`/public/stores/${handle}`)
      .then(async (r) => {
        if (r.data.locked && (savedTok || savedPw)) {
          try {
            const res = await api.post(`/public/stores/${handle}/access`,
              savedTok ? { token: savedTok } : { password: savedPw });
            if (savedTok) sessionStorage.setItem(`sig_store_tok_${handle}`, savedTok);
            else sessionStorage.setItem(`sig_store_pw_${handle}`, savedPw);
            setData(res.data);
            if (tokenParam || keyParam) window.history.replaceState({}, "", `/${handle}`);
            return;
          } catch {
            sessionStorage.removeItem(`sig_store_tok_${handle}`);
            sessionStorage.removeItem(`sig_store_pw_${handle}`);
          }
        }
        setData(r.data);
      })
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [handle]);

  const openProduct = (p) => {
    setSelected(p);
    trackEvent("view_item", "view_item", { store: handle, product: p.title, value: Number(p.price), currency: "EUR" });
  };

  const onLikeToggle = (p) => {
    const adding = !liked.has(p.id);
    toggle(liked, setLiked, p.id);
    if (adding) trackEvent("like", "like", { store: handle, product: p.title });
  };

  const onSaveToggle = (p) => {
    const adding = !saved.has(p.id);
    toggle(saved, setSaved, p.id);
    if (adding) trackEvent("save", "save", { store: handle, product: p.title });
  };

  const onSearchSubmit = () => {
    if (query.trim()) trackEvent("search", "search", { store: handle, query: query.trim() });
  };

  const selectCategory = (cat) => {
    setActiveCat(cat);
    trackEvent("select_category", "select_content", { store: handle, category: cat || "All" });
  };

  const unlock = async (e) => {
    e.preventDefault();
    if (!gatePw) { toast.error("Enter the password"); return; }
    trackEvent("begin_gate_unlock", "begin_gate_unlock", { store: handle });
    setUnlocking(true);
    try {
      const res = await api.post(`/public/stores/${handle}/access`, { password: gatePw });
      sessionStorage.setItem(`sig_store_pw_${handle}`, gatePw);
      setData(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Incorrect password");
    } finally { setUnlocking(false); }
  };

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") { setSelected(null); setCartOpen(false); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const accent = data?.store?.accent_color || "#0095f6";

  const products = useMemo(() => {
    if (!data) return [];
    let list = data.products;
    if (activeCat) list = list.filter((p) => p.category === activeCat);
    if (query) list = list.filter((p) => p.title.toLowerCase().includes(query.toLowerCase()));
    return list;
  }, [data, activeCat, query]);

  const toggle = (set, setSet, id) => {
    const next = new Set(set);
    next.has(id) ? next.delete(id) : next.add(id);
    setSet(next);
  };

  const handleCheckout = async () => {
    if (cart.items.length === 0) return;
    const emailOk = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test((email || "").trim());
    if (!emailOk) {
      toast.error("Please enter a valid email address to continue");
      return;
    }
    setCheckingOut(true);
    try {
      const res = await api.post("/payments/checkout", {
        handle,
        items: cart.items.map((i) => ({ product_id: i.product.id, quantity: i.quantity, variant: i.variant || "", tier_qty: i.bundle ? i.bundle.min_qty : undefined })),
        origin_url: window.location.origin,
        customer_email: email.trim(),
      });
      const cartValue = cart.items.reduce((s, i) => s + cartLineTotal(i), 0);
      trackEvent("checkout_started", "begin_checkout", { store: handle, items: cart.items.length, value: Number(cartValue.toFixed(2)), currency: "EUR" });
      window.location.href = res.data.checkout_url;
    } catch (e) {
      toast.error("Checkout failed. Please try again.");
      setCheckingOut(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <Loader2 className="animate-spin text-zinc-400" />
      </div>
    );
  }
  if (!data) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-white gap-4">
        <Seo title="Store not found — StoreIG" description="This store could not be found on StoreIG." noindex url={`${window.location.origin}/${handle}`} />
        <p className="font-heading text-2xl">Store not found</p>
        <Link to="/" className="text-sm underline">Back to StoreIG</Link>
      </div>
    );
  }

  const { store } = data;
  const canonical = `${window.location.origin}/${handle}`;
  const seoImage = imgUrl(store.banner_url) || imgUrl(store.logo_url) || imgUrl(data.products?.[0]?.image_url);

  if (data.locked) {
    return (
      <>
        <Seo
          title={`${store.name} · Members only — StoreIG`}
          description={store.tagline || `${store.name} is a members-only store on StoreIG.`}
          image={seoImage}
          url={canonical}
          type="website"
          noindex
        />
        <StoreGate store={store} value={gatePw} onChange={setGatePw} onSubmit={unlock} loading={unlocking} />
      </>
    );
  }

  const seoTitle = store.seo_title || `${store.name} — Shop on StoreIG`;
  const seoDescription =
    store.seo_description ||
    store.tagline ||
    `Shop ${store.name} on StoreIG — ${data.products.length} product${data.products.length === 1 ? "" : "s"} available with secure checkout.`;
  const seoJsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "OnlineStore",
      name: store.name,
      description: seoDescription,
      url: canonical,
      ...(seoImage ? { image: seoImage, logo: seoImage } : {}),
    },
    {
      "@context": "https://schema.org",
      "@type": "ItemList",
      name: `${store.name} products`,
      itemListElement: data.products.slice(0, 50).map((p, i) => ({
        "@type": "ListItem",
        position: i + 1,
        item: {
          "@type": "Product",
          name: p.title,
          ...(imgUrl(p.image_url) ? { image: imgUrl(p.image_url) } : {}),
          ...(p.description ? { description: p.description } : {}),
          ...(p.category ? { category: p.category } : {}),
          offers: {
            "@type": "Offer",
            price: Number(p.price).toFixed(2),
            priceCurrency: "EUR",
            availability: (p.stock > 0 || p.in_stock) ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
            url: canonical,
          },
        },
      })),
    },
  ];

  return (
    <div className="min-h-screen bg-white pb-24" style={{ "--accent": accent }}>
      <Seo
        title={seoTitle}
        description={seoDescription}
        image={seoImage}
        url={canonical}
        type="website"
        jsonLd={seoJsonLd}
      />
      <StorefrontPopups />
      {/* App bar */}
      <header className="sticky top-0 z-40 bg-white/80 backdrop-blur-xl border-b border-black/5">
        <div className="max-w-6xl mx-auto px-4 h-16 flex items-center gap-3">
          <Link to="/" className="text-zinc-400 hover:text-black transition-colors" data-testid="store-back-link">
            <ArrowLeft size={20} />
          </Link>
          <div className="font-heading font-bold text-lg tracking-tight truncate">{store.name}</div>
          <div className="flex-1 flex items-center gap-2 bg-zinc-100 rounded-full px-4 py-2 max-w-md ml-auto">
            <Search size={16} className="text-zinc-400" />
            <input
              data-testid="store-search-input"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") onSearchSubmit(); }}
              onBlur={onSearchSubmit}
              placeholder="Search products..."
              className="flex-1 bg-transparent outline-none text-sm"
            />
          </div>
          <button
            data-testid="open-cart-button"
            onClick={() => setCartOpen(true)}
            className="relative p-2 rounded-full hover:bg-zinc-100 transition-colors"
          >
            <ShoppingBag size={22} />
            {count > 0 && (
              <span
                className="absolute -top-0.5 -right-0.5 h-5 min-w-5 px-1 rounded-full text-[11px] font-bold text-white flex items-center justify-center"
                style={{ background: accent }}
                data-testid="cart-count-badge"
              >
                {count}
              </span>
            )}
          </button>
        </div>
      </header>

      {/* Store hero */}
      {store.banner_url ? (
        <div className="max-w-6xl mx-auto px-4 pt-6">
          <div className="rounded-2xl overflow-hidden aspect-[16/6] bg-zinc-100" data-testid="store-banner">
            <img src={imgUrl(store.banner_url)} alt={`${store.name} banner`} className="w-full h-full object-cover" />
          </div>
        </div>
      ) : null}
      <div className="max-w-6xl mx-auto px-4 pt-8 flex items-center gap-5">
        <div className="p-[3px] rounded-full" style={ringStyle(accent)}>
          <div className="bg-white p-[2px] rounded-full">
            <img src={imgUrl(store.logo_url)} alt={store.name} className="h-20 w-20 rounded-full object-cover bg-zinc-100" />
          </div>
        </div>
        <div>
          <div className="font-heading font-semibold text-xl tracking-tight">{store.name}</div>
          <div className="text-sm text-zinc-500">@{store.handle}</div>
          <p className="font-body text-zinc-600 text-sm mt-1 max-w-md">{store.tagline}</p>
        </div>
      </div>

      {/* Stories / categories */}
      <div className="max-w-6xl mx-auto px-4 pt-8">
        <div className="flex gap-5 overflow-x-auto no-scrollbar pb-2">
          <CategoryBubble label="All" active={!activeCat} accent={accent} onClick={() => selectCategory(null)} />
          {data.categories.map((c) => (
            <CategoryBubble key={c} label={c} active={activeCat === c} accent={accent} onClick={() => selectCategory(c)} />
          ))}
        </div>
      </div>

      {/* Product grid */}
      <main className="max-w-6xl mx-auto px-4 pt-6">
        {products.length === 0 ? (
          <p className="text-center text-zinc-400 py-20 font-body">No products found.</p>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-5" data-testid="product-grid">
            {products.map((p, i) => (
              <motion.article
                key={p.id}
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.35, delay: Math.min(i * 0.03, 0.3) }}
                className="group bg-white rounded-2xl border border-zinc-100 p-2 sm:p-3 hover:-translate-y-1 transition-transform duration-300"
                data-testid={`product-card-${p.id}`}
              >
                <div className="relative rounded-xl overflow-hidden aspect-[4/5] bg-zinc-100">
                  <img
                    src={imgUrl(p.image_url)}
                    alt={p.title}
                    onClick={() => openProduct(p)}
                    className="h-full w-full object-cover cursor-pointer transition-transform duration-500 group-hover:scale-105"
                  />
                  {p.stock <= 0 && (
                    <div className="absolute inset-0 bg-white/60 flex items-center justify-center">
                      <span className="bg-black text-white text-xs font-semibold px-3 py-1.5 rounded-full">Sold out</span>
                    </div>
                  )}
                  {p.label ? (
                    <span data-nosnippet data-badge={p.label} data-testid={`product-badge-${p.id}`}
                      className="pd-badge absolute top-2 left-2 z-10 text-[10px] font-heading font-bold uppercase tracking-wider text-white rounded-full px-2.5 py-1 shadow-sm"
                      style={{ background: accent }} />
                  ) : null}
                  {p.is_affiliate ? (
                    <span data-testid={`affiliate-tag-${p.id}`}
                      className={`absolute ${p.label ? "top-10" : "top-2"} left-2 z-10 text-[10px] font-heading font-bold uppercase tracking-wider text-white bg-black/80 rounded-full px-2.5 py-1 shadow-sm`}>
                      Affiliate
                    </span>
                  ) : null}
                  <div className="absolute top-2 right-2 flex flex-col gap-2">
                    <IconBtn active={liked.has(p.id)} onClick={() => onLikeToggle(p)} testid={`like-${p.id}`}>
                      <Heart size={16} className={liked.has(p.id) ? "fill-rose-500 stroke-rose-500 animate-pop" : ""} />
                    </IconBtn>
                    <IconBtn active={saved.has(p.id)} onClick={() => onSaveToggle(p)} testid={`save-${p.id}`}>
                      <Bookmark size={16} className={saved.has(p.id) ? "fill-black stroke-black" : ""} />
                    </IconBtn>
                  </div>
                </div>
                <div className="px-1 pt-3 pb-1">
                  <h3 onClick={() => openProduct(p)} className="font-body font-semibold text-sm leading-tight cursor-pointer line-clamp-1">
                    {p.title}
                  </h3>
                  <div className="flex items-center justify-between mt-2">
                    <span className="font-heading font-bold" style={{ color: accent }}>{money(p.price)}</span>
                    <button
                      data-testid={`add-cart-${p.id}`}
                      disabled={p.stock <= 0 && !p.is_affiliate}
                      onClick={() => {
                        if (p.is_affiliate) { window.open(p.affiliate_url, "_blank", "noopener,noreferrer"); return; }
                        if (p.variant_options?.length) { openProduct(p); return; }
                        addItem(handle, p, ""); toast.success(`${p.title} added`);
                      }}
                      className="text-xs font-semibold text-white rounded-full px-3 py-1.5 active:scale-95 transition-transform disabled:opacity-40"
                      style={{ background: CONV }}
                    >
                      {p.variant_options?.length ? "Select" : "Add"}
                    </button>
                  </div>
                </div>
              </motion.article>
            ))}
          </div>
        )}
      </main>

      <StoreFooter store={store} accent={accent} />

      {/* Product modal */}
      <AnimatePresence>
        {selected && (
          <ProductModal
            product={selected}
            accent={accent}
            onClose={() => setSelected(null)}
            onAdd={(variant, qty = 1, bundle = null) => { addItem(handle, selected, variant, qty, bundle); trackEvent("add_to_cart", "add_to_cart", { store: handle, product: selected.title, value: Number(bundle ? bundle.price : selected.price), currency: "EUR", quantity: qty }); toast.success(bundle ? `${bundle.min_qty}-pack of ${selected.title} added` : (qty > 1 ? `${qty} × ${selected.title} added` : `${selected.title} added`)); setSelected(null); }}
            liked={liked.has(selected.id)}
            saved={saved.has(selected.id)}
            onLike={() => onLikeToggle(selected)}
            onSave={() => onSaveToggle(selected)}
          />
        )}
      </AnimatePresence>

      {/* Cart drawer */}
      <AnimatePresence>
        {cartOpen && (
          <>
            <motion.div
              className="fixed inset-0 bg-black/40 z-50"
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              onClick={() => setCartOpen(false)}
            />
            <motion.aside
              className="fixed right-0 top-0 bottom-0 w-full max-w-md bg-white z-50 flex flex-col"
              initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
              data-testid="cart-drawer"
            >
              <div className="flex items-center justify-between p-6 border-b border-zinc-100">
                <h2 className="font-heading font-semibold text-xl tracking-tight">Your bag</h2>
                <button onClick={() => setCartOpen(false)} data-testid="close-cart-button" className="p-1 hover:bg-zinc-100 rounded-full">
                  <X size={20} />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto p-6 space-y-4">
                {cart.items.length === 0 ? (
                  <p className="text-center text-zinc-400 py-16 font-body">Your bag is empty.</p>
                ) : (
                  cart.items.map((i) => {
                    const k = keyOf(i.product.id, i.variant, i.bundle?.min_qty);
                    const step = i.bundle ? i.bundle.min_qty : 1;
                    const unit = cartUnit(i);
                    const lineTotal = cartLineTotal(i);
                    return (
                    <div key={k} className="flex gap-4" data-testid={`cart-item-${i.product.id}`}>
                      <img src={imgUrl(i.product.image_url)} alt="" className="h-20 w-16 rounded-lg object-cover bg-zinc-100" />
                      <div className="flex-1">
                        <div className="font-body font-semibold text-sm">{i.product.title}</div>
                        {i.variant ? <div className="text-xs text-zinc-400">{i.variant}</div> : null}
                        <div className="text-sm text-zinc-500">
                          {i.bundle ? (
                            <span data-testid={`cart-unit-${i.product.id}`}>
                              <span className="inline-block text-[10px] font-semibold text-white rounded-full px-1.5 py-0.5 mr-1.5 align-middle" style={{ background: CONV }}>{i.bundle.min_qty}-pack</span>
                              <span className="font-semibold text-emerald-600">{money(unit)}</span>
                              {unit < Number(i.product.price) ? <span className="line-through text-zinc-300 ml-1.5">{money(i.product.price)}</span> : null}
                              <span className="ml-1 text-[10px] font-semibold text-emerald-600">ea</span>
                            </span>
                          ) : (
                            <span data-testid={`cart-unit-${i.product.id}`}>{money(unit)}</span>
                          )}
                        </div>
                        <div className="flex items-center gap-3 mt-2">
                          <button onClick={() => setQty(k, i.quantity - step)} className="p-1 border rounded-full" data-testid={`qty-dec-${i.product.id}`}><Minus size={14} /></button>
                          <span className="text-sm w-8 text-center" data-testid={`qty-${i.product.id}`}>{i.quantity}</span>
                          <button onClick={() => setQty(k, i.quantity + step)} className="p-1 border rounded-full" data-testid={`qty-inc-${i.product.id}`}><Plus size={14} /></button>
                          <span className="ml-auto text-sm font-semibold" data-testid={`cart-line-total-${i.product.id}`}>{money(lineTotal)}</span>
                          <button onClick={() => removeItem(k)} className="text-xs text-zinc-400 hover:text-rose-500" data-testid={`remove-${i.product.id}`}>Remove</button>
                        </div>
                      </div>
                    </div>
                    );
                  })
                )}
              </div>
              {cart.items.length > 0 && (
                <div className="border-t border-zinc-100 p-6 space-y-4">
                  <div>
                    <label className="text-xs font-semibold text-zinc-500 block mb-1.5">Email for receipt <span className="text-rose-500">*</span></label>
                    <input
                      data-testid="checkout-email-input"
                      type="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@example.com"
                      className="w-full border border-zinc-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-black"
                    />
                  </div>
                  <div className="flex items-center justify-between font-heading">
                    <span className="text-zinc-500">Total</span>
                    <span className="font-bold text-xl">{money(total)}</span>
                  </div>
                  <button
                    data-testid="checkout-button"
                    onClick={handleCheckout}
                    disabled={checkingOut}
                    className="w-full text-white rounded-full py-4 font-body font-semibold flex items-center justify-center gap-2 active:scale-95 transition-transform disabled:opacity-60"
                    style={{ background: CONV }}
                  >
                    {checkingOut ? <Loader2 size={18} className="animate-spin" /> : "Checkout with Stripe"}
                  </button>
                  <button onClick={clear} className="w-full text-xs text-zinc-400 hover:text-black">Clear bag</button>
                </div>
              )}
            </motion.aside>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}

const StoreFooter = ({ store, accent }) => {
  const LEGAL = [
    { slug: "terms", label: "Terms" },
    { slug: "privacy", label: "Privacy" },
    { slug: "refunds", label: "Returns & Refunds" },
    { slug: "imprint", label: "Contact" },
  ];
  const socials = SOCIAL_ORDER
    .map((key) => ({ key, val: (store.socials || {})[key] }))
    .filter((s) => s.val && String(s.val).trim());
  const socialHref = (key, val) => {
    const v = String(val).trim();
    if (key === "email") return `mailto:${v}`;
    return /^https?:\/\//i.test(v) ? v : `https://${v}`;
  };
  return (
    <footer className="mt-16 border-t border-zinc-100 bg-zinc-50/60" data-testid="store-footer">
      <div className="max-w-6xl mx-auto px-4 py-12">
        <div className="flex flex-col sm:flex-row gap-8 sm:items-start sm:justify-between">
          <div className="flex items-center gap-4">
            <div className="p-[2.5px] rounded-full" style={ringStyle(accent)}>
              <div className="bg-white p-[2px] rounded-full">
                <img src={imgUrl(store.logo_url)} alt={store.name} className="h-12 w-12 rounded-full object-cover bg-zinc-100" />
              </div>
            </div>
            <div>
              <div className="font-heading font-semibold tracking-tight" data-testid="store-footer-name">{store.name}</div>
              <div className="text-xs text-zinc-500">@{store.handle}</div>
              {store.tagline ? <p className="text-xs text-zinc-400 mt-1 max-w-xs leading-relaxed">{store.tagline}</p> : null}
            </div>
          </div>

          <div className="flex flex-col gap-4 sm:items-end">
            {socials.length > 0 && (
              <div className="flex flex-wrap gap-2" data-testid="store-footer-socials">
                {socials.map(({ key, val }) => {
                  const Icon = SOCIAL_ICON[key] || Globe;
                  return (
                    <a key={key} href={socialHref(key, val)} target="_blank" rel="noreferrer" aria-label={key}
                      data-testid={`store-footer-social-${key}`}
                      className="h-9 w-9 rounded-full border border-zinc-200 bg-white flex items-center justify-center text-zinc-600 hover:text-white transition-colors"
                      onMouseEnter={(e) => { e.currentTarget.style.background = accent; e.currentTarget.style.borderColor = accent; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = "#fff"; e.currentTarget.style.borderColor = ""; }}>
                      <Icon size={16} />
                    </a>
                  );
                })}
              </div>
            )}
            <nav className="flex flex-wrap gap-x-6 gap-y-2 text-sm text-zinc-500 sm:justify-end">
              {LEGAL.map((l) => (
                <Link key={l.slug} to={`/legal/${l.slug}`} data-testid={`store-footer-legal-${l.slug}`} className="hover:text-zinc-900 transition-colors">{l.label}</Link>
              ))}
              <button onClick={openCookieSettings} data-testid="store-footer-cookie-settings" className="hover:text-zinc-900 transition-colors">Cookie settings</button>
            </nav>
          </div>
        </div>

        <div className="mt-10 pt-6 border-t border-zinc-100 flex flex-col sm:flex-row items-center justify-between gap-4">
          <span className="text-xs text-zinc-400">© {new Date().getFullYear()} {store.name}. All rights reserved.</span>
          <Link to="/" data-testid="store-footer-poweredby" className="group inline-flex items-center gap-1.5 text-xs text-zinc-400 hover:text-zinc-900 transition-colors">
            Powered by <span className="font-heading font-bold tracking-tight text-zinc-600 group-hover:text-black">StoreIG</span>
          </Link>
        </div>

        <div className="mt-6 rounded-xl border border-zinc-200 bg-zinc-50 p-4 max-w-3xl" data-testid="store-footer-disclaimer-box">
          <div className="flex items-center gap-2 text-zinc-700 mb-1.5">
            <Info size={15} strokeWidth={2} />
            <span className="text-[11px] font-heading font-bold uppercase tracking-[0.2em]">Independent platform</span>
          </div>
          <p className="text-sm leading-relaxed text-zinc-600 font-medium" data-testid="store-footer-disclaimer">
            StoreIG is an independent platform and is not affiliated with, endorsed by, sponsored by, or officially connected to Instagram or Meta Platforms, Inc. Secure payments processed by Stripe.
          </p>
        </div>

        <div className="mt-8 flex flex-col items-center gap-2" data-testid="store-footer-casperbrands">
          <span className="text-[10px] uppercase tracking-[0.3em] text-zinc-300">A brand by</span>
          <a href="https://casperbrands.com" target="_blank" rel="noreferrer" aria-label="CasperBrands">
            <img src={CASPER_LOGO} alt="CasperBrands" data-testid="store-footer-casperbrands-logo" className="h-12 w-auto opacity-70 hover:opacity-100 transition-opacity" />
          </a>
        </div>
      </div>
    </footer>
  );
};

const StoreGate = ({ store, value, onChange, onSubmit, loading }) => (
  <div className="min-h-screen flex items-center justify-center bg-white p-6" data-testid="store-gate">
    <div className="w-full max-w-sm bg-white border border-zinc-200/80 rounded-3xl p-8 text-center animate-fade-up">
      <div className="p-[3px] rounded-full inline-block mb-4" style={ringStyle(store.accent_color || "#0095f6")}>
        <div className="bg-white p-[2px] rounded-full">
          <img src={imgUrl(store.logo_url)} alt={store.name} className="h-16 w-16 rounded-full object-cover bg-zinc-100" />
        </div>
      </div>
      <div className="flex items-center justify-center gap-2 text-zinc-900">
        <Lock size={18} />
        <h1 className="font-heading font-semibold text-2xl tracking-tight">{store.name}</h1>
      </div>
      <p className="text-sm text-zinc-500 mt-2">This store is members only.</p>
      <p className="text-sm text-zinc-500">Enter the password to continue.</p>
      <form onSubmit={onSubmit} className="mt-5 space-y-3">
        <input
          data-testid="store-gate-input"
          type="password"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Store password"
          autoFocus
          className="w-full border border-zinc-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-black text-center"
        />
        <button
          type="submit"
          data-testid="store-gate-submit"
          disabled={loading}
          className="w-full flex items-center justify-center gap-2 casper-btn rounded-full py-3.5 font-body font-semibold disabled:opacity-60"
        >
          {loading && <Loader2 size={16} className="animate-spin" />} Enter store
        </button>
      </form>
      <Link to="/" className="mt-5 inline-block text-sm text-zinc-500 hover:text-black transition-colors" data-testid="store-gate-back">← Back to StoreIG</Link>
    </div>
  </div>
);

const ringStyle = (accent) => ({
  background: `linear-gradient(135deg, ${accent} 0%, ${accent} 30%, #ffffff 50%, ${accent} 70%, ${accent} 100%)`,
});
// consistent high-conversion action button colour across all storefronts
const CONV = "#0a0a0a";

const CategoryBubble = ({ label, active, accent, onClick }) => (  <button onClick={onClick} className="flex flex-col items-center gap-1.5 min-w-[70px] active:scale-95 transition-transform" data-testid={`category-${label}`}>
    <div className={"p-[2.5px] rounded-full"} style={active ? ringStyle(accent) : { background: "#e5e7eb" }}>
      <div className="bg-white p-[2px] rounded-full">
        <div className="h-16 w-16 rounded-full flex items-center justify-center font-heading font-semibold text-xs" style={{ background: "#f4f4f5", color: active ? accent : "#71717a" }}>
          {label.slice(0, 2).toUpperCase()}
        </div>
      </div>
    </div>
    <span className="text-xs font-body truncate max-w-[70px]" style={{ color: active ? "#000" : "#71717a" }}>{label}</span>
  </button>
);

const IconBtn = ({ children, onClick, testid }) => (
  <button onClick={onClick} data-testid={testid} className="h-8 w-8 rounded-full bg-white/90 backdrop-blur flex items-center justify-center shadow-sm active:scale-90 transition-transform">
    {children}
  </button>
);

const ProductModal = ({ product, accent, onClose, onAdd, liked, saved, onLike, onSave }) => {
  const hasVariants = product.variant_name && product.variant_options?.length > 0;
  const [variant, setVariant] = useState("");
  const soldOut = product.stock <= 0;
  const canAdd = !soldOut && (!hasVariants || variant);
  return (
  <>
    <motion.div className="fixed inset-0 bg-black/50 z-50" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose} />
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-6 pointer-events-none">
      <motion.div
        className="bg-white w-full sm:max-w-3xl max-h-[92vh] overflow-y-auto rounded-t-3xl sm:rounded-3xl pointer-events-auto"
        initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 40, opacity: 0 }}
        transition={{ type: "spring", stiffness: 300, damping: 30 }}
        data-testid="product-modal"
      >
        <div className="grid sm:grid-cols-2">
          <div className="relative aspect-square">
            <img src={imgUrl(product.image_url)} alt={product.title} className="h-full w-full object-cover" />
            <button onClick={onClose} data-testid="close-modal-button" className="absolute top-3 right-3 sm:hidden h-9 w-9 rounded-full bg-white/90 flex items-center justify-center"><X size={18} /></button>
          </div>
          <div className="p-6 sm:p-8 relative">
            <button onClick={onClose} data-testid="close-modal-button-desktop" className="absolute top-4 right-4 hidden sm:flex h-9 w-9 rounded-full bg-zinc-100 items-center justify-center hover:bg-zinc-200"><X size={18} /></button>
            <span className="text-xs font-heading font-bold uppercase tracking-[0.2em] text-zinc-400">{product.category}</span>
            {product.is_affiliate ? (
              <span data-testid="modal-affiliate-tag"
                className="ml-2 inline-block align-middle text-[10px] font-heading font-bold uppercase tracking-wider text-white bg-black/80 rounded-full px-2.5 py-1">
                Affiliate
              </span>
            ) : null}
            {product.label ? (
              <span data-nosnippet data-badge={product.label} data-testid="modal-product-badge"
                className="pd-badge block w-fit mt-2 text-[10px] font-heading font-bold uppercase tracking-wider text-white rounded-full px-2.5 py-1"
                style={{ background: accent }} />
            ) : null}
            <h1 className="font-heading font-medium text-3xl tracking-tight mt-2">{product.title}</h1>
            <p className="font-heading font-bold text-2xl mt-3" style={{ color: accent }}>{money(product.price)}</p>
            <p className="font-body text-zinc-600 mt-4 leading-relaxed">{product.description}</p>
            {product.custom_text ? (
              <p data-nosnippet data-text={product.custom_text} data-testid="modal-custom-text"
                 className="pd-text font-body text-sm font-semibold text-zinc-700 mt-3" />
            ) : null}

            {normalizeTiers(product.price_tiers).length > 0 && (
              <div className="mt-5 border border-zinc-200 rounded-2xl overflow-hidden" data-testid="wholesale-table">
                <div className="flex items-center justify-between px-4 py-2.5 bg-zinc-50 border-b border-zinc-100">
                  <span className="text-xs font-heading font-bold uppercase tracking-[0.15em] text-zinc-500">Wholesale bundles</span>
                  <span className="text-[10px] text-zinc-400">tap a bundle to add</span>
                </div>
                <table className="w-full text-sm">
                  <tbody>
                    {normalizeTiers(product.price_tiers).map((t) => {
                      const ea = t.price / t.min_qty;
                      const off = Math.round((1 - ea / Number(product.price)) * 100);
                      return (
                        <tr key={t.min_qty}
                          onClick={() => {
                            if (soldOut) return;
                            if (hasVariants && !variant) { toast.error(`Select ${product.variant_name.toLowerCase()} first`); return; }
                            onAdd(variant, t.min_qty, { min_qty: t.min_qty, price: t.price });
                          }}
                          className="border-b border-zinc-50 last:border-0 cursor-pointer hover:bg-zinc-50 transition-colors group"
                          data-testid={`wholesale-row-${t.min_qty}`}>
                          <td className="px-4 py-2.5 text-zinc-600">{t.min_qty} pieces</td>
                          <td className="px-4 py-2.5 text-right font-semibold" style={{ color: accent }}>{money(t.price)} <span className="text-zinc-400 font-normal text-xs">({money(ea)} ea)</span></td>
                          <td className="px-4 py-2.5 text-right w-24">
                            <span className="inline-flex items-center gap-1.5 justify-end">
                              {off > 0 ? <span className="text-[10px] font-semibold text-emerald-600">-{off}%</span> : null}
                              <span className="h-6 w-6 rounded-full flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-opacity" style={{ background: accent }} data-testid={`wholesale-add-${t.min_qty}`}><Plus size={13} /></span>
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {hasVariants && (
              <div className="mt-5">
                <div className="text-xs font-heading font-bold uppercase tracking-[0.15em] text-zinc-500 mb-2">{product.variant_name}</div>
                <div className="flex flex-wrap gap-2">
                  {product.variant_options.map((o) => (
                    <button
                      key={o}
                      data-testid={`variant-${o}`}
                      onClick={() => setVariant(o)}
                      className={`px-4 py-2 rounded-full border text-sm font-semibold transition-colors ${variant === o ? "bg-black text-white border-black" : "border-zinc-200 text-zinc-600 hover:border-black"}`}
                    >
                      {o}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="mt-4 text-sm font-body" data-testid="modal-stock">
              {soldOut ? <span className="text-rose-500 font-semibold">Sold out</span>
                : product.stock <= 5 ? <span className="text-amber-600 font-semibold">Only {product.stock} left</span>
                : <span className="text-emerald-600 font-semibold">In stock</span>}
            </div>

            <div
              data-testid="modal-action-bar"
              className="flex gap-3 mt-6 sticky bottom-0 -mx-6 px-6 py-4 bg-white/95 backdrop-blur-md border-t border-zinc-100 sm:static sm:mx-0 sm:px-0 sm:py-0 sm:bg-transparent sm:backdrop-blur-none sm:border-0"
            >
              {product.is_affiliate ? (
                <a href={product.affiliate_url} target="_blank" rel="noopener noreferrer nofollow sponsored" data-testid="modal-buy-affiliate" className="flex-1 text-white rounded-full py-3.5 font-body font-semibold text-center active:scale-95 transition-transform" style={{ background: CONV }}>
                  {product.affiliate_source === "amazon" ? "Buy on Amazon" : "Buy on AliExpress"}
                </a>
              ) : (
                <button data-testid="modal-add-cart" disabled={!canAdd} onClick={() => onAdd(variant)} className="flex-1 text-white rounded-full py-3.5 font-body font-semibold active:scale-95 transition-transform disabled:opacity-40" style={{ background: CONV }}>
                  {soldOut ? "Sold out" : hasVariants && !variant ? `Select ${product.variant_name.toLowerCase()}` : `Add to bag · ${money(product.price)}`}
                </button>
              )}
              <button onClick={onLike} data-testid="modal-like" className="h-12 w-12 rounded-full border flex items-center justify-center active:scale-90 transition-transform shrink-0"><Heart size={20} className={liked ? "fill-rose-500 stroke-rose-500" : ""} /></button>
              <button onClick={onSave} data-testid="modal-save" className="h-12 w-12 rounded-full border flex items-center justify-center active:scale-90 transition-transform shrink-0"><Bookmark size={20} className={saved ? "fill-black stroke-black" : ""} /></button>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  </>
  );
};
